import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plane, Newspaper, Camera, Quote, Gamepad2, ExternalLink, ArrowRight } from 'lucide-react';

const AviationHub = () => {
  const [activeTab, setActiveTab] = useState('news');

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A2B4A] via-[#243552] to-[#1A2B4A]">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#1A2B4A]/95 backdrop-blur-sm border-b border-[#4A90E2]/20 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Plane className="h-8 w-8 text-[#4A90E2]" />
              <span className="text-2xl font-heading font-bold text-white">Winglet</span>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/how-it-works" className="text-gray-300 hover:text-[#4A90E2] transition-colors">How It Works</Link>
              <Link to="/pricing" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Pricing</Link>
              <Link to="/compare-tools" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Compare</Link>
              <Link to="/about" className="text-gray-300 hover:text-[#4A90E2] transition-colors">About</Link>              <Link to="/data-privacy" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Data Privacy</Link>              <Link to="/aviation-hub" className="text-[#4A90E2] border-b-2 border-[#4A90E2]">Aviation Hub</Link>
            </div>
            <div className="flex space-x-4">
              <Link to="/login" className="text-gray-300 hover:text-white transition-colors">Sign In</Link>
              <Link to="/signup" className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-6 py-2 rounded-lg transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mb-6">
            Aviation <span className="text-[#4A90E2]">Hub</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            News, photos, quotes, and fun from the world of aviation
          </p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-4 justify-center">
            <TabButton
              active={activeTab === 'news'}
              onClick={() => setActiveTab('news')}
              icon={<Newspaper className="h-5 w-5" />}
              label="Aviation News"
            />
            <TabButton
              active={activeTab === 'photos'}
              onClick={() => setActiveTab('photos')}
              icon={<Camera className="h-5 w-5" />}
              label="Photo Gallery"
            />
            <TabButton
              active={activeTab === 'quotes'}
              onClick={() => setActiveTab('quotes')}
              icon={<Quote className="h-5 w-5" />}
              label="Pilot Quotes"
            />
            <TabButton
              active={activeTab === 'game'}
              onClick={() => setActiveTab('game')}
              icon={<Gamepad2 className="h-5 w-5" />}
              label="Mini Games"
            />
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {activeTab === 'news' && <NewsSection />}
          {activeTab === 'photos' && <PhotoSection />}
          {activeTab === 'quotes' && <QuotesSection />}
          {activeTab === 'game' && <GameSection />}
        </div>
      </section>
    </div>
  );
};

const TabButton = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all ${
      active
        ? 'bg-[#4A90E2] text-white'
        : 'bg-[#1A2B4A] text-gray-300 hover:bg-[#4A90E2]/20 hover:text-white'
    }`}
  >
    {icon}
    <span>{label}</span>
  </button>
);

const NewsSection = () => {
  const newsItems = [
    {
      title: "FAA Announces New Rest Requirements for Regional Carriers",
      source: "Aviation Daily",
      date: "2 days ago",
      excerpt: "The FAA has proposed new regulations extending FAR 117 rest requirements to Part 135 operators...",
      url: "#",
      category: "Regulation"
    },
    {
      title: "Boeing 737 MAX 10 Certification Expected in Q2 2026",
      source: "FlightGlobal",
      date: "1 week ago",
      excerpt: "Boeing projects the 737 MAX 10 will receive FAA certification in the second quarter of 2026...",
      url: "#",
      category: "Aircraft"
    },
    {
      title: "APA Negotiates New Contract with American Airlines",
      source: "ALPA News",
      date: "2 weeks ago",
      excerpt: "The Allied Pilots Association reached a tentative agreement on pay increases and quality-of-life improvements...",
      url: "#",
      category: "Labor"
    },
    {
      title: "Sustainable Aviation Fuel Production Ramps Up in U.S.",
      source: "Aviation Week",
      date: "3 weeks ago",
      excerpt: "New refineries in California and Texas are expected to triple SAF production by year-end...",
      url: "#",
      category: "Environment"
    },
    {
      title: "United Orders 100 Airbus A321neo for Domestic Routes",
      source: "Reuters Aviation",
      date: "1 month ago",
      excerpt: "United Airlines announced a major fleet expansion with Airbus narrowbody aircraft for growth markets...",
      url: "#",
      category: "Fleet"
    },
    {
      title: "NTSB Releases Report on Winter Weather Operations",
      source: "Safety Brief",
      date: "1 month ago",
      excerpt: "The National Transportation Safety Board published new guidance for winter operations following incidents...",
      url: "#",
      category: "Safety"
    }
  ];

  return (
    <div>
      <p className="text-gray-300 text-center mb-8">
        Curated aviation news from trusted sources. Stay informed on industry trends, regulations, and developments.
      </p>
      <div className="grid md:grid-cols-2 gap-6">
        {newsItems.map((item, index) => (
          <NewsCard key={index} {...item} />
        ))}
      </div>
      <div className="mt-12 text-center">
        <p className="text-gray-400 text-sm">
          News content aggregated from public sources. Winglet does not create or endorse news content.
        </p>
      </div>
    </div>
  );
};

const NewsCard = ({ title, source, date, excerpt, url, category }) => (
  <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-6 hover:border-[#4A90E2]/50 transition-all group">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-semibold text-[#4A90E2] uppercase">{category}</span>
      <span className="text-xs text-gray-400">{date}</span>
    </div>
    <h3 className="text-xl font-heading font-bold text-white mb-2 group-hover:text-[#4A90E2] transition-colors">
      {title}
    </h3>
    <p className="text-gray-300 text-sm mb-4">{excerpt}</p>
    <div className="flex items-center justify-between">
      <span className="text-gray-400 text-sm">{source}</span>
      <a 
        href={url} 
        className="flex items-center space-x-1 text-[#4A90E2] hover:text-white transition-colors text-sm font-semibold"
      >
        <span>Read More</span>
        <ExternalLink className="h-4 w-4" />
      </a>
    </div>
  </div>
);

const PhotoSection = () => {
  const photos = [
    {
      title: "Sunrise Over the Atlantic",
      location: "FL380, Somewhere Over the Ocean",
      photographer: "Capt. Mike R.",
      aircraft: "B737-900ER",
      description: "Early morning oceanic crossing en route to London"
    },
    {
      title: "Mountain Approach",
      location: "Denver International",
      photographer: "FO Sarah K.",
      aircraft: "A320neo",
      description: "Final approach with the Rockies in the background"
    },
    {
      title: "Northern Lights at Cruise",
      location: "Polar Route to Asia",
      photographer: "Capt. John D.",
      aircraft: "B777-300ER",
      description: "Aurora borealis from FL420 during polar crossing"
    },
    {
      title: "Thunderstorm Avoidance",
      location: "Miami Center Airspace",
      photographer: "FO Lisa M.",
      aircraft: "B737-800",
      description: "Navigating around towering cumulus buildups"
    },
    {
      title: "Golden Hour Landing",
      location: "San Francisco International",
      photographer: "Capt. Tom W.",
      aircraft: "A321",
      description: "Evening arrival with the Golden Gate in view"
    },
    {
      title: "Cockpit View at Night",
      location: "Somewhere Over Kansas",
      photographer: "FO Alex P.",
      aircraft: "B737-900",
      description: "Night flight with city lights below"
    }
  ];

  return (
    <div>
      <p className="text-gray-300 text-center mb-8">
        Stunning views from the flight deck, submitted by pilots like you.
      </p>
      <div className="grid md:grid-cols-3 gap-6">
        {photos.map((photo, index) => (
          <PhotoCard key={index} {...photo} />
        ))}
      </div>
      <div className="mt-12 text-center">
        <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-8 inline-block">
          <Camera className="h-12 w-12 text-[#4A90E2] mx-auto mb-4" />
          <h3 className="text-xl font-heading font-bold text-white mb-2">Submit Your Photos</h3>
          <p className="text-gray-300 mb-4">
            Got a great shot from the flight deck? Share it with the community!
          </p>
          <Link 
            to="/contact" 
            className="inline-flex items-center space-x-2 text-[#4A90E2] hover:text-white transition-colors font-semibold"
          >
            <span>Contact Us to Submit</span>
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const PhotoCard = ({ title, location, photographer, aircraft, description }) => (
  <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl overflow-hidden group hover:border-[#4A90E2]/50 transition-all">
    <div className="h-48 bg-gradient-to-br from-[#4A90E2]/40 to-[#1A2B4A] flex items-center justify-center">
      <Camera className="h-16 w-16 text-[#4A90E2]/50" />
    </div>
    <div className="p-4">
      <h3 className="text-lg font-heading font-bold text-white mb-1">{title}</h3>
      <p className="text-sm text-[#4A90E2] mb-2">{location}</p>
      <p className="text-sm text-gray-300 mb-3">{description}</p>
      <div className="flex items-center justify-between text-xs text-gray-400">
        <span>{photographer}</span>
        <span className="text-[#4A90E2]">{aircraft}</span>
      </div>
    </div>
  </div>
);

const QuotesSection = () => {
  const quotes = [
    {
      text: "The engine is the heart of an airplane, but the pilot is its soul.",
      author: "Walter Raleigh",
      role: "Aviation Pioneer"
    },
    {
      text: "A mile of highway will take you a mile. A mile of runway will take you anywhere.",
      author: "Unknown",
      role: "Aviation Wisdom"
    },
    {
      text: "Flying is more than a sport, more than a job; flying is pure passion and desire.",
      author: "General Adolf Galland",
      role: "Luftwaffe Ace"
    },
    {
      text: "The only time you have too much fuel is when you're on fire.",
      author: "Unknown",
      role: "Pilot Saying"
    },
    {
      text: "Good judgment comes from experience. Experience comes from bad judgment.",
      author: "Bob Hoover",
      role: "Test Pilot Legend"
    },
    {
      text: "There are old pilots, and there are bold pilots. But there are no old, bold pilots.",
      author: "Unknown",
      role: "Aviation Proverb"
    },
    {
      text: "The best safety device is a careful pilot.",
      author: "Unknown",
      role: "Safety First"
    },
    {
      text: "Aviation is proof that given the will, we have the capacity to achieve the impossible.",
      author: "Eddie Rickenbacker",
      role: "WWI Flying Ace"
    }
  ];

  return (
    <div>
      <p className="text-gray-300 text-center mb-8">
        Words of wisdom from aviation legends and the cockpit community.
      </p>
      <div className="grid md:grid-cols-2 gap-6">
        {quotes.map((quote, index) => (
          <QuoteCard key={index} {...quote} />
        ))}
      </div>
    </div>
  );
};

const QuoteCard = ({ text, author, role }) => (
  <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-6 hover:border-[#4A90E2]/50 transition-all">
    <Quote className="h-8 w-8 text-[#4A90E2]/50 mb-4" />
    <p className="text-lg text-white italic mb-4">"{text}"</p>
    <div className="border-t border-[#4A90E2]/20 pt-4">
      <p className="text-white font-semibold">{author}</p>
      <p className="text-gray-400 text-sm">{role}</p>
    </div>
  </div>
);

const GameSection = () => {
  return (
    <div className="text-center">
      <div className="max-w-2xl mx-auto">
        <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-12">
          <Gamepad2 className="h-24 w-24 text-[#4A90E2] mx-auto mb-6" />
          <h2 className="text-3xl font-heading font-bold text-white mb-4">Coming Soon</h2>
          <p className="text-gray-300 mb-6">
            We're building fun aviation mini-games to help you pass the time during layovers.
          </p>
          <div className="space-y-4 text-left max-w-md mx-auto">
            <GamePreview 
              title="Airport Code Challenge"
              description="How many IATA codes can you identify in 60 seconds?"
            />
            <GamePreview 
              title="Landing Simulator"
              description="Test your landing skills with realistic physics"
            />
            <GamePreview 
              title="FAR 117 Quiz"
              description="Test your knowledge of rest and duty regulations"
            />
          </div>
          <div className="mt-8">
            <Link 
              to="/contact" 
              className="inline-flex items-center space-x-2 text-[#4A90E2] hover:text-white transition-colors font-semibold"
            >
              <span>Suggest a Game Idea</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0F1824] border-t border-gray-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Branding */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Plane className="h-6 w-6 text-[#4A90E2]" />
                <span className="text-xl font-heading font-bold text-white">Winglet</span>
              </div>
              <p className="text-gray-400 text-sm">The AI-powered co-pilot for your career.</p>
            </div>
            
            {/* Product Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/how-it-works" className="text-gray-400 hover:text-[#4A90E2] transition-colors">How It Works</Link></li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Pricing</Link></li>
                <li><Link to="/compare-tools" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Compare</Link></li>
              </ul>
            </div>
            
            {/* Company Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-gray-400 hover:text-[#4A90E2] transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Contact</Link></li>
                <li><Link to="/data-privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Data Privacy</Link></li>
              </ul>
            </div>
            
            {/* Legal Section */}
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>© 2026 Winglet. Built by pilots, for pilots. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const GamePreview = ({ title, description }) => (
  <div className="bg-[#0F1824] border border-[#4A90E2]/20 rounded-lg p-4">
    <h3 className="text-white font-semibold mb-1">{title}</h3>
    <p className="text-gray-400 text-sm">{description}</p>
  </div>
);

export default AviationHub;
